import type { FlowchartData } from "@/models/flowchart";

export type IdPrefix = "PROC" | "ACT" | "FR" | "BR";
export type CollectionIdPrefix = "ROLE" | "STK" | "TERM";

const FLOWCHART_ID_REGEX = /^(PROC|ACT|FR|BR)-(\d+)$/;

type WithId = {
  id?: string | number | null;
};

/**
 * Scans all existing IDs in a single FlowchartData for the given prefix,
 * finds the highest sequence number, and returns the next ID.
 *
 * Use this in flowchart.tsx where you already have the current data object.
 *
 * Format: <PREFIX>-<zero-padded 3-digit number>
 * Example: ACT-007
 *
 * Falls back gracefully if data is empty or malformed.
 */
export function generateId(data: FlowchartData, prefix: IdPrefix): string {
  const existingNumbers = collectExistingNumbers(data, prefix);
  const next = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
  return formatId(prefix, next);
}

/**
 * Scans ALL tabs (domains) in a menu item to guarantee project-wide unique IDs.
 *
 * Use this in projectHeader.tsx where a new domain is being created from scratch
 * and there is no single FlowchartData to scan — instead we scan every existing
 * tab's data to find the global highest sequence number.
 *
 * @param tabs  - The menuItemData.tabs array (each tab has a .data: FlowchartData)
 * @param prefix - The ID prefix to generate (PROC | ACT | FR | BR)
 *
 * Example:
 *   const proc1 = generateIdAcrossTabs(menuItemData.tabs, "PROC"); // → "PROC-003"
 */
export function generateIdAcrossTabs(
  tabs: Array<{ data?: FlowchartData }> | undefined,
  prefix: IdPrefix
): string {
  const allNumbers: number[] = (tabs ?? []).flatMap((tab) =>
    tab.data ? collectExistingNumbers(tab.data, prefix) : []
  );
  const next = allNumbers.length > 0 ? Math.max(...allNumbers) + 1 : 1;
  return formatId(prefix, next);
}

export function generateCollectionId<T extends WithId>(
  items: T[] | undefined,
  prefix: CollectionIdPrefix
): string {
  const existingNumbers = collectCollectionNumbers(items, prefix);
  const next = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
  return formatId(prefix, next);
}

export function normalizeCollectionIds<T extends WithId>(
  items: T[] | undefined,
  prefix: CollectionIdPrefix
): { items: T[]; idMap: Record<string, string> } {
  if (!Array.isArray(items)) {
    return { items: [], idMap: {} };
  }

  const existingNumbers = collectCollectionNumbers(items, prefix);
  let nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
  const usedIds = new Set<string>();
  const idMap: Record<string, string> = {};

  const normalizedItems = items.map((item) => {
    const rawId =
      item?.id === undefined || item?.id === null ? "" : String(item.id).trim();
    const existingNumber = getCollectionNumber(rawId, prefix);
    const canReuseId = rawId.length > 0 && existingNumber !== null && !usedIds.has(rawId);

    let normalizedId = rawId;

    if (!canReuseId) {
      normalizedId = formatId(prefix, nextNumber);
      nextNumber += 1;

      while (usedIds.has(normalizedId)) {
        normalizedId = formatId(prefix, nextNumber);
        nextNumber += 1;
      }
    }

    usedIds.add(normalizedId);

    if (rawId && rawId !== normalizedId) {
      idMap[rawId] = normalizedId;
    }

    return {
      ...item,
      id: normalizedId,
    };
  });

  return { items: normalizedItems, idMap };
}

function formatId(prefix: IdPrefix | CollectionIdPrefix, n: number): string {
  return `${prefix}-${String(n).padStart(3, "0")}`;
}

/**
 * Collects all sequence numbers already in use for a given prefix
 * by traversing the full FlowchartData tree.
 */
function collectExistingNumbers(data: FlowchartData, prefix: IdPrefix): number[] {
  const numbers: number[] = [];

  const tryCollect = (id: unknown) => {
    if (typeof id !== "string") return;
    const match = id.match(FLOWCHART_ID_REGEX);
    if (match && match[1] === prefix) {
      numbers.push(parseInt(match[2], 10));
    }
  };

  const processes = data?.processes ?? [];

  for (const process of processes) {
    // PROC
    tryCollect(process.id);

    const stages = process.stages ?? [];
    for (const stage of stages) {
      // ACT
      tryCollect(stage.id);

      const taskGroups = stage.taskGroups ?? [];
      for (const group of taskGroups) {
        const tasks = group.tasks ?? [];
        for (const task of tasks) {
          // FR
          tryCollect(task.id);

          const businessRules = task.businessRules ?? [];
          for (const rule of businessRules) {
            // BR
            if (typeof rule === "object" && rule !== null) {
              tryCollect((rule as { id?: string }).id);
            }
          }
        }
      }

      // BR on stage level (if applicable)
      const stageBRs = (stage as any).businessRules ?? [];
      for (const rule of stageBRs) {
        if (typeof rule === "object" && rule !== null) {
          tryCollect((rule as { id?: string }).id);
        }
      }
    }
  }

  return numbers;
}

function collectCollectionNumbers<T extends WithId>(
  items: T[] | undefined,
  prefix: CollectionIdPrefix
): number[] {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .map((item) => getCollectionNumber(item?.id, prefix))
    .filter((value): value is number => value !== null);
}

function getCollectionNumber(id: unknown, prefix: CollectionIdPrefix): number | null {
  if (typeof id !== "string" && typeof id !== "number") {
    return null;
  }

  const match = String(id)
    .trim()
    .match(new RegExp(`^${prefix}-(\\d+)$`));

  return match ? parseInt(match[1], 10) : null;
}
